import React, { useState, Children } from 'react';
import propTypes from 'prop-types';
import ErrorsList from 'components/ErrorsList';
import Switch from 'components/Switch';
import classNames from 'lib/classNames';
import { SideBarBadge } from './Sidebar';
import Button from './Button';

const FormControl = ({
    label,
    labelColor = 'text-gray-600',
    children,
    className = '',
    marginBottom = 'mb-6',
    info = null,
    error = null,
    showError = true,
    styles = {
        container: {},
        label: {}
    },
    withSwitch,
    childrenWidth = 'w-1/2',
    singleChildWidth = 'w-full',
    isSingleCol,
    beta,
    action,
    Footer,
    disabled = false,
}) => {
    const isErrorArray = Array.isArray(error);
    const isString = error => typeof error === 'string';
    const hasErrors = (isErrorArray ? error.filter(isString).length : error) && !!showError;
    const getErrors = isErrorArray ? error.filter(isString) : error;

    const containerClasses = classNames(
        className,
        'relative',
        {
            [marginBottom]: Children.count(children) === 1 || isSingleCol,
            'opacity-50': disabled
        }
    );

    const labelClasses = classNames(
        'flex items-center block flex-1 flex-grow capitalize text-sm font-bold',
        labelColor,
        {
            'error': !!hasErrors && !disabled,
            'text-gray-400': disabled
        }
    );

    const infoClasses = classNames(
        'text-gray-500 text-xs italic mb-1',
        {
            'error': !!hasErrors && !disabled,
            'text-gray-400': disabled
        }
    );

    return (
        <div className={containerClasses}>
            {disabled && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-200 rounded"></div>
            )}
            {!!label && (
                <div
                    className={classNames('flex items-center mb-1', {
                        'mb-3': Children.count(children) && withSwitch
                    })}
                >
                    <label className={labelClasses}>
                        {label}
                        {disabled && (
                            <span
                                className={classNames(
                                    "ml-2 flex-center tracking-wider py-1 px-2 rounded-lg text-black text-xs font-bold"
                                )}
                                style={{ backgroundColor: "#ffbf6e" }}
                            >
                                Deprecated
                            </span>
                        )}
                    </label>
                    {(beta && !disabled) && (
                        <span
                            className={classNames(
                                'flex-center bg-orange-500 tracking-wider py-1 px-2 rounded-lg text-white text-xxs font-bold',
                                {
                                    'mr-2': withSwitch
                                }
                            )}
                            style={{ top: -2, right: -2 }}
                        >
                            Beta
                        </span>
                    )}
                    {!!withSwitch && <Switch disabled={disabled} BindToContext={withSwitch} />}
                    {!withSwitch && action && (
                        <Button
                            disabled={disabled}
                            type="link-danger"
                            icon="trash"
                            size="medium"
                            onClick={action}
                            style={{ top: -2, right: -2 }}
                        />
                    )}
                </div>
            )}
            {!!info && (
                <p
                    className={infoClasses}
                    dangerouslySetInnerHTML={{ __html: info.replace(/\n/g, '<br/>') }}
                />
            )}
            {Children.count(children) > 1 && !isSingleCol && (
                <div
                    className={classNames('flex flex-wrap -mx-2', {
                        'mb-2': !!hasErrors
                    })}
                >
                    {Children.map(children, (child, i) => (
                        <div className={classNames('px-2', childrenWidth, marginBottom)}>
                            {child}
                        </div>
                    ))}
                </div>
            )}
            {(Children.count(children) === 1 || isSingleCol) && (
                <div className={classNames(singleChildWidth)}>
                    {children}
                </div>
            )}
            {!!Footer && <Footer />}
            {!!hasErrors && !disabled && (
                <ErrorsList
                    {...{ error: getErrors, showFirstOnly: true }}
                    style={{ marginTop: 10 }}
                />
            )}
        </div>
    );
};

FormControl.propTypes = {
    label: propTypes.string,
    labelColor: propTypes.string,
    className: propTypes.string,
    marginBottom: propTypes.string,
    info: propTypes.string,
    error: propTypes.oneOfType([
        propTypes.string,
        propTypes.arrayOf(propTypes.string)
    ]),
    showError: propTypes.bool,
    styles: propTypes.object,
    withSwitch: propTypes.object,
    childrenWidth: propTypes.string,
    singleChildWidth: propTypes.string,
    isSingleCol: propTypes.bool,
    beta: propTypes.bool,
    action: propTypes.func,
    Footer: propTypes.func,
    disabled: propTypes.bool
};

FormControl.defaultProps = {
    info: false,
    style: {},
    disabled: false
};

export default FormControl;